import React, { RefObject, useMemo, useRef } from "react";

//types
import { CouponMessageCard, OfferCardType } from "@core-ui/interfaces/Common";

//constants
import { CLOSE_ICON, ACCESSIBILITY_TEXTS, PLUS_ICON, PLACEHOLDER_TEXT_FOR_IMG_INSERT } from "./constants";

//styles
import {
    Container,
    Icon,
    CloseIcon,
    Title,
    Description,
    Subtitle,
    SecondarySavingsSubtitle,
    SecondaryDiscount,
    CTA,
    PlusIcon as PlusIconContainer,
} from "./styles";

//helpers
import { isEmpty } from "@core-ui/helpers/Utils";
import { getCloudinaryUrl } from "@core-ui/helpers/Image";
import { APPLY_COUPON_MODAL_TEST_IDS, getAriaLabel } from "./helpers";
import { getActiveElement } from "@core-ui/helpers/ActiveElement";

//components
import { CustomCloseIcon } from "@core-ui/components/common/custom-close-icon";
import PlusIcon from "@core-ui/components/common/plus-icon";
import { AriaHidden, ScreenReaderOnly } from "@core-ui/components/accessibility";
import { Image } from "@core-ui/components/image";
import { DialogHOC } from "@core-ui/components/dialog";

//hooks
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";
import { useFocusDialog } from "@core-ui/hooks/useFocusDialog";

export interface CartModalProps {
    data: CouponMessageCard;
    closeModal: VoidFunction;
}

const CartModal: React.FC<CartModalProps> = ({ data, closeModal }: CartModalProps) => {
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);
    const primarySavingsText = data.primarySavingsInfo?.text || data.text;
    const primarySavingsSubtext = data.primarySavingsInfo?.subtext || data.subtext;
    const primaryTextToShowInMiddle = `${primarySavingsText ?? ""} ${primarySavingsSubtext ?? ""}`.trim();
    const secondarySavingsText = data.secondarySavingsInfo?.text;
    const secondarySavingsSubtext = data.secondarySavingsInfo?.subtext;
    const secondaryTextToShowInMiddle = `${secondarySavingsText ?? ""} ${secondarySavingsSubtext ?? ""}`.trim();

    useEffectOnce(() => {
        previouslyFocusedElement.current = getActiveElement();
    });

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    const cartModalAriaLabel = useMemo(() => getAriaLabel(primaryTextToShowInMiddle, data), [
        data,
        primaryTextToShowInMiddle,
    ]);

    // check if an image is provided, check if the primary text has the placeholder text for image insertion
    // if both are true, then insert the image html inside the primary text html
    const [finalPrimaryText, isHTMLInsertionNeeded] = useMemo(() => {
        if (
            !isEmpty(data?.attributeValues?.logo) &&
            !isEmpty(primaryTextToShowInMiddle) &&
            primaryTextToShowInMiddle.includes(PLACEHOLDER_TEXT_FOR_IMG_INSERT)
        ) {
            const imageHTMLStr = `<div style="display: inline-block; vertical-align: middle; min-width: 28px; height: 28px;"><img style="height: 16px;" src="${getCloudinaryUrl(
                data?.attributeValues?.logo,
            )}" data-testid="${APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_IMG}"/></div>`;
            return [
                `<div>${primaryTextToShowInMiddle.replace(PLACEHOLDER_TEXT_FOR_IMG_INSERT, imageHTMLStr)}</div>`,
                true,
            ];
        }
        return [primaryTextToShowInMiddle, false];
    }, [data?.attributeValues, primaryTextToShowInMiddle]);

    const isPHApplyCoupon = data?.offer_card_type === OfferCardType.OFFER_CARD_TYPE_POCKET_HERO;

    return (
        <Container
            isPH={isPHApplyCoupon}
            aria-modal="true"
            data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL}
        >
            <AriaHidden as="div">
                {!isEmpty(data.icon) && (
                    <Icon isPH={isPHApplyCoupon}>
                        <Image imageId={data.icon} lazy={false} width={48} />
                    </Icon>
                )}
                <CloseIcon>
                    <CustomCloseIcon
                        onClick={closeModal}
                        IconBgColor={CLOSE_ICON.color}
                        IconFillColor={CLOSE_ICON.fill}
                        size={CLOSE_ICON.size}
                    />
                </CloseIcon>
                {/* always show when ux3 but for ux4 when there's no text in middle it looks weird,
             so we make this title text as the text to be shown in big font and not render this */}
                {primaryTextToShowInMiddle && !isPHApplyCoupon && (
                    <Title
                        variant="V2.heading1.neutral.black"
                        data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_TEXT}
                        dangerouslySetInnerHTML={{ __html: data.title }}
                    />
                )}
                {/* use case where image insertion is not needed, directly add text as the child for the parent div */}
                {(primaryTextToShowInMiddle || data.title) && !isHTMLInsertionNeeded && (
                    <Subtitle
                        noMarginTop={!primaryTextToShowInMiddle}
                        data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_PRIMARY_TEXT}
                    >
                        {primaryTextToShowInMiddle || data.title}
                    </Subtitle>
                )}
                {/* use case where an image is provided in data for insertion into the primary text, 
                need to convert data into HTML string to have image html inline */}
                {(primaryTextToShowInMiddle || data.title) && isHTMLInsertionNeeded && (
                    <Subtitle
                        noMarginTop={!primaryTextToShowInMiddle}
                        dangerouslySetInnerHTML={{ __html: finalPrimaryText || data.title }}
                    />
                )}
                {!isEmpty(primaryTextToShowInMiddle) && !isEmpty(secondaryTextToShowInMiddle) && (
                    <>
                        <PlusIconContainer as="span">
                            <AriaHidden as="span">
                                <PlusIcon iconFillColor={PLUS_ICON.fill} />
                            </AriaHidden>
                        </PlusIconContainer>
                        <SecondarySavingsSubtitle>{secondaryTextToShowInMiddle}</SecondarySavingsSubtitle>
                    </>
                )}
                {!isEmpty(primaryTextToShowInMiddle) && !isEmpty(data.secondaryDiscountCallout) && (
                    <SecondaryDiscount>{data.secondaryDiscountCallout}</SecondaryDiscount>
                )}
                {!isEmpty(data.description) && (
                    <Description
                        variant="V2.body2.neutral.medium"
                        data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_DESCRIPTION}
                        dangerouslySetInnerHTML={{ __html: data.description }}
                    />
                )}
            </AriaHidden>
            <ScreenReaderOnly text={cartModalAriaLabel} as="div" compRef={headerDivRef as RefObject<HTMLDivElement>} />
            <CTA
                style={{ color: data.cta.color }}
                onClick={closeModal}
                data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_CLOSE}
                aria-label={ACCESSIBILITY_TEXTS.GO_BACK_TO_CART}
            >
                {data.cta.text}
            </CTA>
        </Container>
    );
};

export default DialogHOC(React.memo(CartModal));
